// src/components/MainHeader.js
import React, {
  useContext,
useState,
useEffect,
useMemo,
Suspense,
lazy,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { apiRequest } from "../services/api";
import { getMenuDrawerCategories } from "../services/categories.api";
import { getHeaderOverlayImages } from "../services/homepage.api";

const MenuDrawer = lazy(() => import("./MenuDrawer"));
const SearchOverlay = lazy(() => import("./SearchOverlay"));

function MainHeader() {
  const { lang, toggleLanguage } = useContext(LanguageContext);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [headerCategories, setHeaderCategories] = useState([]);
  const [hoveredDesktopGroup, setHoveredDesktopGroup] = useState(null);
  const [headerOverlayImages, setHeaderOverlayImages] = useState(null);
  const { cartItems, total, removeFromCart, wishlist } = useCart();
  const navigate = useNavigate();
const location = useLocation();
const [isDesktopHeader, setIsDesktopHeader] = useState(() =>
  typeof window !== "undefined" ? window.innerWidth >= 1024 : false
);

  const [isLoggedIn, setIsLoggedIn] = useState(
    !!localStorage.getItem("esill-token")
  );

  const [accountUser, setAccountUser] = useState(() => {
  try {
    return JSON.parse(localStorage.getItem("esill-user")) || null;
  } catch {
    return null;
  }
});

const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
const [isCartMenuOpen, setIsCartMenuOpen] = useState(false);
  useEffect(() => {
  const loadAccountUser = async () => {
    const token = localStorage.getItem("esill-token");
    setIsLoggedIn(!!token);

    if (!token) {
      setAccountUser(null);
      setIsAccountMenuOpen(false);
      return;
    }

    try {
      const cachedUser = localStorage.getItem("esill-user");

      if (cachedUser) {
        setAccountUser(JSON.parse(cachedUser));
      }

      const freshUser = await apiRequest("/users/me");
      localStorage.setItem("esill-user", JSON.stringify(freshUser));
      setAccountUser(freshUser);
    } catch {
      setAccountUser(null);
    }
  };

  loadAccountUser();

  window.addEventListener("auth-changed", loadAccountUser);
  return () => window.removeEventListener("auth-changed", loadAccountUser);
}, []);

useEffect(() => {
  const updateHeaderMode = () => {
  setIsDesktopHeader(window.innerWidth >= 1024);
};

  updateHeaderMode();
  window.addEventListener("resize", updateHeaderMode);

  return () => window.removeEventListener("resize", updateHeaderMode);
}, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
  let mounted = true;

  getMenuDrawerCategories()
    .then((cats) => {
      if (!mounted) return;
      setHeaderCategories(Array.isArray(cats) ? cats : []);
    })
    .catch(() => {});

  return () => {
    mounted = false;
  };
}, []);

useEffect(() => {
  let mounted = true;

  getHeaderOverlayImages()
    .then((data) => {
      if (!mounted) return;
      setHeaderOverlayImages(data || {});
    })
    .catch(() => {});

  return () => {
    mounted = false;
  };
}, []);

  const handleLoginClick = () => {
    localStorage.setItem("esill-open-login", "1");
    window.dispatchEvent(new Event("open-entry-modal"));
  };

  const handleLogoutClick = () => {
    localStorage.removeItem("esill-token");
    localStorage.removeItem("esill-user");
    window.dispatchEvent(new Event("auth-changed"));
    navigate("/");
  };

  const iconButtonClass =
    "w-9 h-9 flex items-center justify-center rounded-md transition-all duration-200";

    const desktopIconButtonClass =
  "w-12 h-12 flex items-center justify-center rounded-md transition-all duration-200";

const desktopActionClass =
  "flex flex-col items-center justify-start gap-1 min-w-[78px]";

const desktopActionLabelClass =
  "text-[10px] leading-none tracking-wide text-gray-500 whitespace-nowrap";

  const SearchBar = ({ className = "", desktop = false }) => (
  <button
    type="button"
    onClick={() => setIsSearchOpen(true)}
    className={`
      flex items-center justify-between
      border
      text-sm
      transition-all duration-200
      overflow-hidden
      ${
        desktop
          ? "h-12 rounded-none border-black bg-white pl-4 pr-0"
          : "h-9 rounded-md px-3"
      }
      ${className}
    `}
    style={{
      borderColor: desktop ? "#000000" : "#9CA3AF",
      background: "#fff",
      color: "#000000",
    }}
  >
    <span className={desktop ? "text-[15px] text-black" : ""}>
      {desktop ? "Search  in store..." : "Search"}
    </span>

    <span
      className={`
        flex items-center justify-center shrink-0
        ${desktop ? "w-12 h-full bg-black" : "w-auto h-auto"}
      `}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        className={desktop ? "w-6 h-6 text-white" : "w-5 h-5 text-[#111827] opacity-80"}
        aria-hidden="true"
      >
        <circle
          cx="10.5"
          cy="10.5"
          r="6"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path
          d="M15.5 15.5L21 21"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </span>
  </button>
);

  const Logo = ({ className = "" }) => (
  <img
    src="/icons/logoEtika.png"
    alt="ETIKA"
    className={`object-contain ${className}`}
  />
);

const HEADER_TEXT = {
  EN: {
    newIn: "NEW IN",
    bestseller: "BESTSELLER",
    sale: "SALE",
    allProducts: "ALL PRODUCTS",
    clothes: "CLOTHES",
    shoes: "SHOES",
    accessories: "ACCESSORIES",
    empty: "No collections yet",
  },
  AR: {
    newIn: "وصل حديثاً",
    bestseller: "الأكثر مبيعاً",
    sale: "تخفيضات",
    allProducts: "كل المنتجات",
    clothes: "ملابس",
    shoes: "أحذية",
    accessories: "إكسسوارات",
    empty: "لا توجد مجموعات حالياً",
  },
}[lang];

const desktopMenuCollections = useMemo(() => {
  const normalized = Array.isArray(headerCategories) ? headerCategories : [];

  return {
    none: normalized.filter((c) => (c.menu_group || "none") === "none"),
    clothes: normalized.filter((c) => c.menu_group === "clothes"),
    shoes: normalized.filter((c) => c.menu_group === "shoes"),
    accessories: normalized.filter((c) => c.menu_group === "accessories"),
    sale: normalized,
  };
}, [headerCategories]);

const getHeaderTargetPath = (target) => {
  if (!target) return "";

  const params = new URLSearchParams();

  // ✅ keep language in the URL immediately
  // so LanguageUrlSync does not need to rewrite it after navigation
  params.set("lang", lang);

  if (target === "all-products") {
    return `/all-products?${params.toString()}`;
  }

  // ✅ header navigation must always start clean:
  // no old tag, no selectedCategory, no search, no filters
  params.set("category", target);

  return `/products?${params.toString()}`;
};

const navigateHeaderTarget = (target) => {
  const nextPath = getHeaderTargetPath(target);
  if (!nextPath) return;

  const currentPath = `${location.pathname}${location.search}`;

  setHoveredDesktopGroup(null);

  const resetKey = Date.now();

  navigate(nextPath, {
    // ✅ If same URL, replace instead of pushing many entries.
    // Still updates location.state so the page can reset filters.
    replace: currentPath === nextPath,
    state: {
      from: "header",
      resetFiltersFromHeader: true,
      resetKey,
    },
  });

  window.scrollTo({ top: 0, behavior: "smooth" });
};

const openHeaderCollection = (slug) => {
  navigateHeaderTarget(slug);
};

const desktopGroupItems = [
  desktopMenuCollections.clothes.length > 0 && {
    key: "clothes",
    label: HEADER_TEXT.clothes,
    group: "clothes",
  },
  desktopMenuCollections.shoes.length > 0 && {
    key: "shoes",
    label: HEADER_TEXT.shoes,
    group: "shoes",
  },
  desktopMenuCollections.accessories.length > 0 && {
    key: "accessories",
    label: HEADER_TEXT.accessories,
    group: "accessories",
  },
].filter(Boolean);

const desktopSecondRowButtons = [
  {
    key: "new-in",
    label: HEADER_TEXT.newIn,
    slug: "new-in",
    direct: true,
    className: "font-bold",
  },
  {
    key: "bestseller",
    label: HEADER_TEXT.bestseller,
    slug: "bestsellers",
    direct: true,
    className: "font-medium",
  },
    {
    key: "sale",
    label: HEADER_TEXT.sale,
    slug: "sale",
    direct: true,
    sale: true,
    className: "font-bold",
  },
  {
    key: "all-products",
    label: HEADER_TEXT.allProducts,
    slug: "all-products",
    direct: true,
    allProducts: true,
    className: "font-bold",
  },
  ...desktopGroupItems,
  ...desktopMenuCollections.none.map((cat) => ({
    key: cat.slug,
    slug: cat.slug,
    label: lang === "AR" ? cat.name_ar : cat.name_en,
    direct: true,
    className: "font-medium",
  })),
];

const shouldScrollSecondRow = desktopSecondRowButtons.length > 12;

const hoveredDesktopCollections = hoveredDesktopGroup
  ? desktopMenuCollections[hoveredDesktopGroup] || []
  : [];

  const accountFirstName =
  accountUser?.full_name?.trim()?.split(/\s+/)?.[0] || "";

const desktopAccountLabel =
  isLoggedIn && accountFirstName
    ? `HELLO ${accountFirstName.toUpperCase()}`
    : lang === "AR"
    ? "حسابك"
    : "YOUR ACCOUNT";

const ACCOUNT_MENU_TEXT = {
  EN: {
    myAccount: "MY ACCOUNT",
    contactHelp: "CONTACT/HELP",
    signOut: "SIGN OUT",
  },
  AR: {
    myAccount: "حسابي",
    contactHelp: "التواصل/المساعدة",
    signOut: "تسجيل الخروج",
  },
}[lang];

const signOutFromHeader = () => {
  localStorage.removeItem("esill-token");
  localStorage.removeItem("esill-user");
  setIsLoggedIn(false);
  setAccountUser(null);
  setIsAccountMenuOpen(false);
  window.dispatchEvent(new Event("auth-changed"));
  navigate("/");
};

const CART_TEXT = {
  EN: {
    qty: "Quantity",
    total: "Total",
    delivery:
      "Delivery fees are calculated at checkout. Free pickup from store is available.",
    checkout: "Check out »",
    empty: "Your bag is empty.",
  },
  AR: {
    qty: "الكمية",
    total: "الإجمالي",
    delivery:
      "يتم احتساب رسوم التوصيل عند إتمام الطلب. الاستلام من المتجر متاح مجاناً.",
    checkout: "إتمام الشراء »",
    empty: "الحقيبة فارغة.",
  },
}[lang];

const isLoggedInUser = () => {
  return Boolean(localStorage.getItem("esill-token"));
};

const openEntryModalForGuest = () => {
  localStorage.removeItem("esill-open-login");
  window.dispatchEvent(new Event("open-entry-modal"));
};

const goToCartPage = () => {
  if (!isLoggedInUser()) {
    setIsCartMenuOpen(false);
    openEntryModalForGuest();
    return;
  }

  apiRequest("/analytics/track", {
    method: "POST",
    body: JSON.stringify({
      action_type: "open_cart_page",
      entity_type: "cart",
    }),
  }).catch(() => {});

  setIsCartMenuOpen(false);

  const nextPath = `/cart?lang=${lang}`;
  const isAlreadyCartPage = location.pathname === "/cart";

  navigate(nextPath, {
    replace: isAlreadyCartPage,
  });
};

const truncateCartName = (name = "") => {
  const safeName = String(name || "");
  return safeName.length > 10 ? `${safeName.slice(0, 10)}...` : safeName;
};

const isVideoMediaUrl = (url = "") => {
  const cleanUrl = String(url || "").split("?")[0].toLowerCase();
  return /\.(mp4|webm|ogg|mov)$/i.test(cleanUrl);
};

const HeaderCartMedia = ({ item, itemName }) => {
  const src = item.image || "/images/placeholder-product.jpg";

  if (isVideoMediaUrl(src)) {
    return (
      <video
        src={src}
        muted
        playsInline
        preload="metadata"
        className="
          w-14 h-14
          object-cover
          rounded-md
          border border-white
          bg-white
          shrink-0
        "
      />
    );
  }

  return (
    <img
      src={src}
      alt={itemName || ""}
      className="
        w-14 h-14
        object-cover
        rounded-md
        border border-white
        bg-white
        shrink-0
      "
    />
  );
};

const getHeaderCartItemMergeKey = (item) => {
  const isSpecial =
    item.isBxgyFree ||
    item.isBxgyDiscounted ||
    item.bxgyType === "free" ||
    item.bxgyType === "percentage";

  // ✅ Normal cart products must merge only by product + variant
  // This fixes: qty 3 row + old qty 1 row showing separately
  if (!isSpecial) {
    return [
      "normal",
      Number(item.product_id || 0),
      Number(item.variant_id || 0),
    ].join("-");
  }

  // ✅ Keep special BXGY/free/discount rows separated safely
  return [
    "special",
    Number(item.product_id || 0),
    Number(item.variant_id || 0),
    item.bxgyType || (item.isBxgyFree ? "free" : item.isBxgyDiscounted ? "percentage" : "none"),
    Number(item.bxgyDiscountRawValue || item.bxgyDiscountValue || 0),
  ].join("-");
};

const headerCartItems = useMemo(() => {
  const map = new Map();

  cartItems.forEach((item) => {
    const key = getHeaderCartItemMergeKey(item);

    const itemIds = Array.isArray(item.cart_item_ids)
      ? item.cart_item_ids
      : [item.id];

    if (!map.has(key)) {
      map.set(key, {
        ...item,
        qty: Number(item.qty || 0),
        cart_item_ids: itemIds,
      });
      return;
    }

    const existing = map.get(key);

    map.set(key, {
      ...existing,

      // ✅ Use latest/highest backend qty, not sum.
      // Fixes: old row qty 1 + new row qty 3 => display 3, not 4.
      qty: Math.max(Number(existing.qty || 0), Number(item.qty || 0)),

      // ✅ Keep all backend row IDs so X removes every duplicate row
      cart_item_ids: [
        ...(existing.cart_item_ids || []),
        ...itemIds,
      ].filter(Boolean),
    });
  });

  return Array.from(map.values());
}, [cartItems]);

 const cartCount = headerCartItems.reduce(
  (sum, item) => sum + Number(item.qty || 0),
  0
);

const wishlistCount = Array.isArray(wishlist) ? wishlist.length : 0;

const handleHeaderRemoveFromCart = async (e, item) => {
  e.stopPropagation();

  try {
    const idsToRemove =
      Array.isArray(item.cart_item_ids) && item.cart_item_ids.length > 0
        ? item.cart_item_ids
        : [item.id];

    for (const id of idsToRemove) {
      await removeFromCart(id);
    }
  } catch {}
};

const resolveHeaderImage = (url = "") => {
  if (!url) return "";
  if (String(url).startsWith("http")) return url;

  const base =
    process.env.REACT_APP_API_URL?.replace(/\/api$/, "") ||
    window.location.origin;

  return base + url;
};

const hoveredDesktopOverlayData =
  hoveredDesktopGroup && headerOverlayImages
    ? headerOverlayImages[hoveredDesktopGroup] || null
    : null;

const hoveredDesktopOverlayImage = hoveredDesktopOverlayData?.image_url || "";
const hoveredDesktopOverlayTarget = hoveredDesktopOverlayData?.link_target || "";

const openHeaderOverlayTarget = (target) => {
  navigateHeaderTarget(target);
};

  return (
    <>
      <div
  dir="ltr"
  className={`
    left-0 w-full
    px-4 transition-all duration-300
    z-[9998]
    bg-white
    relative lg:fixed
py-3 lg:${isScrolled ? "py-2 shadow-md" : "py-3 shadow-sm"}
    translate-y-0
  `}
  style={{
  top: isScrolled ? "0px" : isDesktopHeader ? "60px" : "0px",
  borderBottom: isDesktopHeader
    ? "1px solid rgba(0,0,0,0.06)"
    : "none",
  boxShadow: isDesktopHeader
    ? isScrolled
      ? "0 10px 20px rgba(0,0,0,0.08)"
      : "0 6px 14px rgba(0,0,0,0.06)"
    : "none",
}}
>
        {/* DESKTOP HEADER: ONE ROW / THREE COLUMNS */}
        <div className="hidden lg:grid grid-cols-3 items-center w-full min-h-[60px]">
          {/* LEFT: MENU + SEARCH BAR */}
<div className="flex items-center gap-3 justify-start">
  <SearchBar desktop className="w-[315px] text-base justify-start" />
</div>

          {/* CENTER: LOGO */}
          <div
            className={`
              flex flex-col items-center justify-center
              transition-all duration-300
              ${isScrolled ? "scale-90" : "scale-100"}
            `}
            style={{ cursor: "pointer" }}
            onClick={() => {
              navigate("/");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
<Logo className="h-12 w-auto" />
          </div>

          {/* RIGHT: LANGUAGE + ACCOUNT + WISHLIST + CART */}
<div className="flex items-start gap-4 justify-end">
  {/* LANGUAGE */}
  <div className={desktopActionClass}>
    <button
      type="button"
      aria-label={lang === "EN" ? "Switch to Arabic" : "Switch to English"}
      onClick={toggleLanguage}
      title={lang === "EN" ? "Switch to Arabic" : "Switch to English"}
      className={`${desktopIconButtonClass} relative`}
      style={{
        color: "#111827",
        background: "transparent",
      }}
    >
      <svg
        width="29"
        height="29"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#1F2937"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20" />
        <path d="M12 2a15.3 15.3 0 0 1 0 20" />
        <path d="M12 2a15.3 15.3 0 0 0 0 20" />
      </svg>

      <span
        className="
          absolute bottom-[5px] -right-0.5
          min-w-[20px] h-[20px]
          px-[3px]
          flex items-center justify-center
          rounded-full
          text-[9px] font-bold
        "
        style={{
          background: "#fff",
          color: "#111827",
          border: "1px solid rgba(0,0,0,0.18)",
          lineHeight: 1,
          boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
        }}
      >
        {lang}
      </span>
    </button>

    <span className={desktopActionLabelClass}>{lang}</span>
  </div>

  {/* ACCOUNT */}
<div
  className={`${desktopActionClass} relative`}
  onMouseEnter={() => {
    if (isLoggedIn) setIsAccountMenuOpen(true);
  }}
  onMouseLeave={() => setIsAccountMenuOpen(false)}
>
  <button
    type="button"
    aria-label="Account"
    onClick={() => {
      apiRequest("/analytics/track", {
        method: "POST",
        body: JSON.stringify({
          action_type: "open_account_from_menu",
          entity_type: "navigation",
          entity_id: null,
        }),
      }).catch(() => {});

      const token = localStorage.getItem("esill-token");
      if (!token) navigate("/create-account");
      else navigate("/account");
    }}
    className={desktopIconButtonClass}
    style={{ background: "transparent" }}
  >
    <svg
      width="30"
      height="30"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#1F2937"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21a8 8 0 0 0-16 0" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  </button>

  <span className={desktopActionLabelClass}>
    {desktopAccountLabel}
  </span>

  {isLoggedIn && isAccountMenuOpen && (
    <div
      dir={lang === "AR" ? "rtl" : "ltr"}
      className="
        absolute top-6 right-0
        mt-2
        w-[170px]
        bg-white
        border border-gray-200
        rounded-xl
        shadow-[0_14px_35px_rgba(0,0,0,0.14)]
        p-3
        z-[10000]
      "
    >
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => {
            setIsAccountMenuOpen(false);
            navigate("/account");
          }}
          className="
            w-full h-9
            rounded-md
            border border-black
            bg-white
            text-black
            text-[12px] font-semibold
            tracking-wide
            hover:bg-black hover:text-white
            transition
          "
        >
          {ACCOUNT_MENU_TEXT.myAccount}
        </button>

        <button
          type="button"
          onClick={() => {
            setIsAccountMenuOpen(false);
            navigate("/contact");
          }}
          className="
            w-full h-9
            rounded-md
            border border-black
            bg-white
            text-black
            text-[12px] font-semibold
            tracking-wide
            hover:bg-black hover:text-white
            transition
          "
        >
          {ACCOUNT_MENU_TEXT.contactHelp}
        </button>

        <button
          type="button"
          onClick={signOutFromHeader}
          className="
            w-full h-9
            rounded-md
            border border-black
            bg-white
            text-black
            text-[12px] font-semibold
            tracking-wide
            hover:bg-black hover:text-white
            transition
          "
        >
          {ACCOUNT_MENU_TEXT.signOut}
        </button>
      </div>
    </div>
  )}
</div>

  {/* WISHLIST */}
  <div className={desktopActionClass}>
    <button
  type="button"
  aria-label="Wishlist"
  onClick={() => navigate("/wishlist")}
  className={`${desktopIconButtonClass} relative`}
  style={{ background: "transparent" }}
>
      <svg
        width="30"
        height="30"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#1F2937"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
      </svg>
      {wishlistCount > 0 && (
  <span
    className="
      absolute -top-1 -right-2
      min-w-[22px] h-[22px]
      px-[5px]
      rounded-full
      bg-black
      text-white
      text-[11px]
      font-medium
      flex items-center justify-center
      leading-none
    "
  >
    {wishlistCount}
  </span>
)}
    </button>

    <span className={desktopActionLabelClass}>FAVORITE</span>
  </div>

  {/* CART */}
<div
  className={`${desktopActionClass} relative`}
  onMouseEnter={() => {
    if (cartCount > 0) setIsCartMenuOpen(true);
  }}
  onMouseLeave={() => setIsCartMenuOpen(false)}
>
  <button
    type="button"
    id="main-cart-icon"
    aria-label="Open cart"
    onClick={goToCartPage}
    className={`${desktopIconButtonClass} relative`}
    style={{
      transition: "background 200ms ease",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = "rgba(251,192,45,0.15)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "transparent";
    }}
  >
    <img
      src="/icons/bag.png"
      alt="Bag"
      className="w-8 h-8 transition-transform duration-300"
    />

    <span className="cart-badge">{cartCount}</span>
  </button>

  <span className={desktopActionLabelClass}>YOUR BAG</span>

  {cartCount > 0 && isCartMenuOpen && (
    <div
      dir={lang === "AR" ? "rtl" : "ltr"}
      className="
        absolute top-6 right-0
        mt-2
        w-[330px]
        bg-black
        rounded-xl
        shadow-[0_18px_45px_rgba(0,0,0,0.28)]
        p-4
        z-[10000]
      "
    >
      <div className="max-h-[310px] overflow-y-auto pr-1 space-y-3">
        {headerCartItems.map((item) => {
  const itemName = lang === "AR" ? item.nameAR : item.nameEN;

  return (
    <div
      key={`${item.product_id}-${item.variant_id}-${item.bxgyType || "none"}-${item.bxgyDiscountRawValue || item.bxgyDiscountValue || 0}`}
      className="flex items-center gap-3"
    >
      <HeaderCartMedia item={item} itemName={itemName} />

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 text-[12px] leading-5">
                  <span className="text-gray-400">
                    {CART_TEXT.qty} {item.qty}
                  </span>

                  <span className="text-white font-semibold">
                    {truncateCartName(itemName)}
                  </span>
                </div>

                <p className="text-white text-[13px] font-semibold mt-1">
  {item.isBxgyFree || item.bxgyType === "free"
    ? lang === "AR"
      ? "مجاني"
      : "FREE"
    : `$${Number(item.price || 0).toFixed(2)}`}
</p>

{(item.isBxgyDiscounted || item.bxgyType === "percentage") && (
  <p className="mt-0.5 text-[11px] text-green-300 font-semibold">
    {lang === "AR"
      ? `خصم ${Number(item.bxgyDiscountValue || item.bxgyDiscountRawValue || 0)}%`
      : `${Number(item.bxgyDiscountValue || item.bxgyDiscountRawValue || 0)}% OFF`}
  </p>
)}
              </div>

              <button
                type="button"
                onClick={(e) => handleHeaderRemoveFromCart(e, item)}
                className="
                  w-6 h-6
                  rounded-full
                  bg-white
                  text-black
                  flex items-center justify-center
                  text-[16px] leading-none
                  font-bold
                  shrink-0
                  hover:scale-105
                  transition
                "
                aria-label="Remove item"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>

      <div className="h-px bg-white/80 my-4" />

      <div className="flex items-center justify-between text-white text-sm font-bold">
        <span>{CART_TEXT.total}</span>
        <span>${Number(total || 0).toFixed(2)}</span>
      </div>

      <div className="mt-4 bg-white text-black rounded-md px-3 py-2 text-[12px] leading-5">
        {CART_TEXT.delivery}
      </div>

      <button
        type="button"
        onClick={goToCartPage}
        className="
          w-full
          mt-4
          h-11
          rounded-md
          bg-[#FBC02D]
          text-black
          text-sm
          font-bold
          tracking-wide
          hover:brightness-95
          active:scale-[0.99]
          transition
        "
      >
        {CART_TEXT.checkout}
      </button>
    </div>
  )}
</div>
</div>
        </div>

{/* DESKTOP SECOND ROW */}
<div
  className="hidden lg:block mt-1 pt-3 relative"
  style={{
    borderTop: "1px solid #9CA3AF",
  }}
  onMouseLeave={() => setHoveredDesktopGroup(null)}
>
  <div
    dir={lang === "AR" ? "rtl" : "ltr"}
    className={`
      flex items-center gap-6
      ${shouldScrollSecondRow ? "justify-start overflow-x-auto scrollbar-hide" : "justify-center"}
    `}
  >
    {desktopSecondRowButtons.map((item) => {
      const isGroup = !!item.group;
      const isActiveGroup = hoveredDesktopGroup === item.group;

      return (
        <button
          key={item.key}
          type="button"
          onMouseEnter={() => {
            if (isGroup) setHoveredDesktopGroup(item.group);
            else setHoveredDesktopGroup(null);
          }}
  onClick={() => {
  if (item.allProducts) {
    apiRequest("/analytics/track", {
      method: "POST",
      body: JSON.stringify({
        action_type: "enter_all_products",
        entity_type: "navigation",
        entity_id: null,
      }),
    }).catch(() => {});
  }

  if (item.direct && item.slug) {
    navigateHeaderTarget(item.slug);
  }
}}
          className={`
  shrink-0
  px-4 py-2
  text-sm tracking-wide
  whitespace-nowrap
  uppercase
  transition
  ${item.className || "font-medium"}
  ${isActiveGroup ? "underline underline-offset-4" : ""}
`}
          style={{
  background: item.sale
    ? "hsl(0, 70%, 46%)"
    : item.allProducts
    ? "#FFF4BF"
    : "#fff",
  color: item.sale ? "#fff" : "#111827",
}}
        >
          {String(item.label || "").toUpperCase()}
        </button>
      );
    })}
  </div>

  {/* DESKTOP HOVER OVERLAY */}
  {hoveredDesktopGroup && (
    <div
      dir={lang === "AR" ? "rtl" : "ltr"}
      onMouseEnter={() => setHoveredDesktopGroup(hoveredDesktopGroup)}
      className="
        absolute left-0 right-0 top-full
        bg-white
        border-t border-gray-200
        shadow-[0_18px_35px_rgba(0,0,0,0.12)]
        z-[9999]
      "
    >
      <div className="max-w-[1180px] mx-auto px-8 py-5">
  <div className="grid grid-cols-[1fr_420px] gap-10 items-start">
    <div>
      {hoveredDesktopCollections.length > 0 ? (
        <div className="grid grid-cols-2 gap-x-16 gap-y-3">
          {hoveredDesktopCollections.map((cat) => (
            <button
              key={cat.slug}
              type="button"
              onClick={() => {
                setHoveredDesktopGroup(null);
                openHeaderCollection(cat.slug);
              }}
              className={`
                text-sm text-gray-900 hover:underline underline-offset-4 uppercase
                ${lang === "AR" ? "text-right" : "text-left"}
              `}
            >
              {String(
                (lang === "AR" ? cat.name_ar : cat.name_en) || ""
              ).toUpperCase()}
            </button>
          ))}
        </div>
      ) : (
        <div className="text-sm text-gray-500">
          {HEADER_TEXT.empty}
        </div>
      )}
    </div>

    {hoveredDesktopOverlayImage && (
  <div className="w-full">
    <button
      type="button"
      disabled={!hoveredDesktopOverlayTarget}
      onClick={() => openHeaderOverlayTarget(hoveredDesktopOverlayTarget)}
      className={`
        block
        w-full
        text-left
        overflow-hidden
        bg-gray-100
        border border-gray-100
        transition
        ${
          hoveredDesktopOverlayTarget
            ? "cursor-pointer hover:opacity-90"
            : "cursor-default"
        }
      `}
      title={hoveredDesktopOverlayTarget || ""}
    >
      <img
        src={resolveHeaderImage(hoveredDesktopOverlayImage)}
        alt=""
        className="
          w-full
          h-[390px]
          object-cover
          bg-gray-100
        "
      />
    </button>
  </div>
)}
  </div>
</div>
    </div>
  )}
</div>

{/* MOBILE HEADER: THREE ROWS */}
<div className="flex md:hidden flex-col w-full">
          {/* ROW 1: LOGO */}
          <div
            className="flex flex-col items-center justify-center"
            style={{ cursor: "pointer" }}
            onClick={() => {
              navigate("/");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <Logo className="h-10 w-auto" />
          </div>

          {/* ROW 2: ICONS SPREAD EDGE TO EDGE */}
          <div className="mt-0 flex items-center justify-between w-full">
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => {
                setIsDrawerOpen(true);
              }}
              className={iconButtonClass}
              style={{
                transition: "transform 150ms ease",
              }}
            >
              <div className="space-y-[4px] transition-all duration-200">
                <span
                  className="block w-[22px] h-[2px]"
                  style={{ background: "#1F2937" }}
                />
                <span
                  className="block w-[22px] h-[2px]"
                  style={{ background: "#1F2937" }}
                />
                <span
                  className="block w-[22px] h-[2px]"
                  style={{ background: "#1F2937" }}
                />
              </div>
            </button>

            <button
              type="button"
              aria-label={
                lang === "EN" ? "Switch to Arabic" : "Switch to English"
              }
              onClick={toggleLanguage}
              title={lang === "EN" ? "Switch to Arabic" : "Switch to English"}
              className={`${iconButtonClass} relative`}
              style={{
  color: "#111827",
  background: "transparent",
}}
            >
              <svg
                width="23"
                height="23"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1F2937"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20" />
                <path d="M12 2a15.3 15.3 0 0 1 0 20" />
                <path d="M12 2a15.3 15.3 0 0 0 0 20" />
              </svg>

              <span
                className="
  absolute bottom-[3px] -right-0.5
  min-w-[18px] h-[18px]
  px-[3px]
  flex items-center justify-center
  rounded-full
  text-[9px] font-bold
"
                style={{
  background: "#fff",
  color: "#111827",
  border: "1px solid rgba(0,0,0,0.18)",
  lineHeight: 1,
  boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
}}
              >
                {lang}
              </span>
            </button>

            <button
  type="button"
  aria-label="Account"
  onClick={() => {
    apiRequest("/analytics/track", {
      method: "POST",
      body: JSON.stringify({
        action_type: "open_account_from_menu",
        entity_type: "navigation",
        entity_id: null,
      }),
    }).catch(() => {});

    const token = localStorage.getItem("esill-token");
    if (!token) navigate("/create-account");
    else navigate("/account");
  }}
  className={iconButtonClass}
  style={{ background: "transparent" }}
>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1F2937"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 21a8 8 0 0 0-16 0" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </button>

            <button
  type="button"
  aria-label="Wishlist"
  onClick={() => navigate("/wishlist")}
  className={`${iconButtonClass} relative`}
  style={{ background: "transparent" }}
>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1F2937"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
              </svg>
              {wishlistCount > 0 && (
  <span
    className="
      absolute -top-1 -right-2
      min-w-[20px] h-[20px]
      px-[5px]
      rounded-full
      bg-black
      text-white
      text-[11px]
      font-medium
      flex items-center justify-center
      leading-none
    "
  >
    {wishlistCount}
  </span>
)}
            </button>

            <button
              type="button"
              id="main-cart-icon"
              aria-label="Open cart"
              onClick={goToCartPage}
              className={`${iconButtonClass} relative`}
              style={{
                transition: "background 200ms ease",
              }}
            >
              <img
                src="/icons/bag.png"
                alt="Bag"
                className="w-6 h-6 transition-transform duration-300"
              />
              <span className="cart-badge">{cartCount}</span>
            </button>
          </div>

          {/* ROW 3: SEARCH BAR */}
          <div className="mt-2 w-full">
            <SearchBar className="w-full justify-start" />
          </div>
        </div>
{/* TABLET HEADER: COMPACT TOUCH LAYOUT */}
<div className="hidden md:flex lg:hidden flex-col w-full">
  {/* ROW 1: LOGO CENTER */}
  <div
    className="flex flex-col items-center justify-center"
    style={{ cursor: "pointer" }}
    onClick={() => {
      navigate("/");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }}
  >
    <Logo className="h-12 w-auto" />
  </div>

  {/* ROW 2: ICONS */}
  <div className="mt-1 flex items-center justify-between w-full max-w-[720px] mx-auto">
    <button
      type="button"
      aria-label="Open menu"
      onClick={() => {
        setIsDrawerOpen(true);
      }}
      className="w-11 h-11 flex items-center justify-center rounded-md transition-all duration-200"
      style={{
        transition: "transform 150ms ease",
      }}
    >
      <div className="space-y-[5px] transition-all duration-200">
        <span className="block w-[26px] h-[2px]" style={{ background: "#1F2937" }} />
        <span className="block w-[26px] h-[2px]" style={{ background: "#1F2937" }} />
        <span className="block w-[26px] h-[2px]" style={{ background: "#1F2937" }} />
      </div>
    </button>

    <button
      type="button"
      aria-label={lang === "EN" ? "Switch to Arabic" : "Switch to English"}
      onClick={toggleLanguage}
      title={lang === "EN" ? "Switch to Arabic" : "Switch to English"}
      className="w-11 h-11 flex items-center justify-center rounded-md transition-all duration-200 relative"
      style={{
        color: "#111827",
        background: "transparent",
      }}
    >
      <svg
        width="25"
        height="25"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#1F2937"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20" />
        <path d="M12 2a15.3 15.3 0 0 1 0 20" />
        <path d="M12 2a15.3 15.3 0 0 0 0 20" />
      </svg>

      <span
        className="
          absolute bottom-[4px] -right-0.5
          min-w-[19px] h-[19px]
          px-[3px]
          flex items-center justify-center
          rounded-full
          text-[9px] font-bold
        "
        style={{
          background: "#fff",
          color: "#111827",
          border: "1px solid rgba(0,0,0,0.18)",
          lineHeight: 1,
          boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
        }}
      >
        {lang}
      </span>
    </button>

    <button
      type="button"
      aria-label="Account"
      onClick={() => {
        apiRequest("/analytics/track", {
          method: "POST",
          body: JSON.stringify({
            action_type: "open_account_from_menu",
            entity_type: "navigation",
            entity_id: null,
          }),
        }).catch(() => {});

        const token = localStorage.getItem("esill-token");
        if (!token) navigate("/create-account");
        else navigate("/account");
      }}
      className="w-11 h-11 flex items-center justify-center rounded-md transition-all duration-200"
      style={{ background: "transparent" }}
    >
      <svg
        width="26"
        height="26"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#1F2937"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20 21a8 8 0 0 0-16 0" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    </button>

    <button
      type="button"
      aria-label="Wishlist"
      onClick={() => navigate("/wishlist")}
      className="w-11 h-11 flex items-center justify-center rounded-md transition-all duration-200 relative"
      style={{ background: "transparent" }}
    >
      <svg
        width="26"
        height="26"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#1F2937"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
      </svg>

      {wishlistCount > 0 && (
        <span
          className="
            absolute -top-1 -right-2
            min-w-[20px] h-[20px]
            px-[5px]
            rounded-full
            bg-black
            text-white
            text-[11px]
            font-medium
            flex items-center justify-center
            leading-none
          "
        >
          {wishlistCount}
        </span>
      )}
    </button>

    <button
      type="button"
      id="main-cart-icon-tablet"
      aria-label="Open cart"
      onClick={goToCartPage}
      className="w-11 h-11 flex items-center justify-center rounded-md transition-all duration-200 relative"
      style={{
        transition: "background 200ms ease",
      }}
    >
      <img
        src="/icons/bag.png"
        alt="Bag"
        className="w-7 h-7 transition-transform duration-300"
      />
      <span className="cart-badge">{cartCount}</span>
    </button>
  </div>

  {/* ROW 3: SEARCH BAR */}
  <div className="mt-2 w-full max-w-[720px] mx-auto">
    <SearchBar className="w-full justify-start h-10" />
  </div>
</div>
      </div>

      {/* MENU DRAWER */}
{isDrawerOpen && (
  <Suspense fallback={null}>
    <MenuDrawer
      isOpen={isDrawerOpen}
      onClose={() => setIsDrawerOpen(false)}
    />
  </Suspense>
)}

{/* SEARCH OVERLAY */}
{isSearchOpen && (
  <Suspense fallback={null}>
    <SearchOverlay
      isOpen={isSearchOpen}
      onClose={() => {
        setIsSearchOpen(false);
        apiRequest("/analytics/track", {
          method: "POST",
          body: JSON.stringify({
            action_type: "close_search",
            entity_type: "navigation",
            entity_id: null,
          }),
        }).catch(() => {});
      }}
    />
  </Suspense>
)}
    </>
  );
}

export default MainHeader;
